import React, { useState } from 'react'
import { X, Plane, Calendar, Hash, MapPin, Loader2, CheckCircle2 } from 'lucide-react'
import { Button } from '../ui/Button'
import { mealmateService } from '../../services/mealmateService'
import './MealmateSection.css'

interface TravelCoordinationModalProps {
  isOpen: boolean
  onClose: () => void
  partnerId?: string
  partnerName?: string
}

const TravelCoordinationModal: React.FC<TravelCoordinationModalProps> = ({
  isOpen,
  onClose,
  partnerId,
  partnerName
}) => {
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [formData, setFormData] = useState({
    travelType: 'flight' as 'flight' | 'hotel',
    referenceNumber: '',
    startDate: '',
    endDate: '',
    location: '',
  })

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!partnerId) return

    try {
      setLoading(true)
      const response = await mealmateService.coordinateTravelMeal({
        partnerId,
        travelType: formData.travelType,
        referenceNumber: formData.referenceNumber,
        startDate: new Date(formData.startDate).toISOString(),
        endDate: formData.endDate ? new Date(formData.endDate).toISOString() : undefined,
        location: formData.location,
        mealFee: 25, // Standard BIOTUNE Coordination Fee
      })

      if (response.success) {
        setSuccess(true)
        setTimeout(() => {
          onClose()
          setSuccess(false)
        }, 2000)
      }
    } catch (error) {
      console.error('Coordination failed:', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content travel-modal" onClick={e => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          <X size={24} />
        </button>

        {success ? (
          <div className="success-state">
            <CheckCircle2 size={64} className="text-green-500 mb-4" />
            <h2>Coordination Successful!</h2>
            <p>Your precision specs have been sent to {partnerName}.</p>
          </div>
        ) : (
          <>
            <div className="modal-header">
              <Plane className="header-icon" />
              <div>
                <h2>Coordinate Travel Meal</h2>
                <p>Personalize your experience with {partnerName}</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="travel-form">
              <div className="form-group">
                <label>Travel Type</label>
                <div className="radio-group">
                  <label className={formData.travelType === 'flight' ? 'active' : ''}>
                    <input 
                      type="radio" 
                      value="flight" 
                      checked={formData.travelType === 'flight'} 
                      onChange={() => setFormData({...formData, travelType: 'flight'})}
                    />
                    Flight
                  </label>
                  <label className={formData.travelType === 'hotel' ? 'active' : ''}>
                    <input 
                      type="radio" 
                      value="hotel" 
                      checked={formData.travelType === 'hotel'} 
                      onChange={() => setFormData({...formData, travelType: 'hotel'})}
                    />
                    Hotel
                  </label>
                </div>
              </div>

              <div className="form-grid">
                <div className="form-group">
                  <label><Calendar size={16} /> Start Date</label>
                  <input 
                    type="datetime-local" 
                    required 
                    value={formData.startDate}
                    onChange={e => setFormData({...formData, startDate: e.target.value})}
                  />
                </div>
                {formData.travelType === 'hotel' && (
                  <div className="form-group">
                    <label><Calendar size={16} /> End Date</label>
                    <input 
                      type="datetime-local" 
                      value={formData.endDate}
                      onChange={e => setFormData({...formData, endDate: e.target.value})}
                    />
                  </div>
                )}
              </div>

              <div className="form-group">
                <label><Hash size={16} /> Reference Number (Flight # or Booking ID)</label>
                <input 
                  type="text" 
                  placeholder="e.g. EK202 or B-12345" 
                  value={formData.referenceNumber}
                  onChange={e => setFormData({...formData, referenceNumber: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label><MapPin size={16} /> Location / Airport</label>
                <input 
                  type="text" 
                  placeholder="e.g. DXB or London Emirates Palace" 
                  value={formData.location}
                  onChange={e => setFormData({...formData, location: e.target.value})}
                />
              </div>

              <div className="fee-notice">
                <span>Coordination Fee: <strong>$25.00</strong></span>
                <p>Includes high-resolution precision spec export and chef briefing.</p>
              </div>

              <Button 
                type="submit" 
                className="w-full mt-4" 
                disabled={loading}
              >
                {loading ? <Loader2 className="animate-spin" /> : 'Confirm Coordination'}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  )
}

export default TravelCoordinationModal
